import { CrossingGame } from './CrossingGame.js';
import { CrossingRenderer } from './CrossingRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable } from '../../input/KeyboardController.js';

export class CrossingInput extends BaseInput<CrossingGame, CrossingRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: CrossingGame,
        renderer: CrossingRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);

        // Cancel pending teleporter placement on right-click
        if (this.game.getPendingTeleporter()) {
            this.game.cancelPending();
            this.onUpdate();
            return;
        }

        const cell = this.detector.detectCell(x, y);
        if (cell) {
            const pos = { row: cell.row, col: cell.col };
            if (this.game.removeTeleporterAt(pos)) {
                this.onUpdate();
            }
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        const cell = this.detector.detectCell(x, y);
        if (cell) {
            if (this.gateMode && isVortexGame(this.game)) {
                if (this.game.toggleVortexGate(cell)) {
                    this.onUpdate();
                }
            } else {
                const changed = this.game.handleCellClick({ row: cell.row, col: cell.col });
                if (changed) {
                    this.onUpdate();
                }
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Place = click (place/complete/toggle); delete = cancel pending or remove a
    // pair. The trace anchors the first teleporter and confines the cursor to valid
    // second cells, committing the pair on release.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        return this.game.handleCellClick(pos);
    }

    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getPendingTeleporter()) { this.game.cancelPending(); return true; }
        return this.game.removeTeleporterAt(pos);
    }

    usesConstrainedTrace(): boolean {
        return true;
    }

    traceHasPending(): boolean {
        return this.game.getPendingTeleporter() !== null;
    }

    traceCanAnchor(pos: Position): boolean {
        return this.game.isValidTeleporterPosition(pos);
    }

    traceAnchor(pos: Position): void {
        this.game.handleCellClick(pos); // sets the pending first teleporter
    }

    isTraceTarget(anchor: Position, pos: Position): boolean {
        return this.game.getValidPairPositions(anchor)
            .some(p => p.row === pos.row && p.col === pos.col);
    }

    traceCommit(_anchor: Position, current: Position): boolean {
        // The pending first teleporter is already down (from traceAnchor); clicking
        // a valid second completes the pair.
        return this.game.handleCellClick(current);
    }

    /** `X` goes to the first digit — where a solution has to start. */
    cursorOrigin(): Position | null {
        const grid = this.game.getGrid();
        return grid.getDigitPosition(grid.minDigit);
    }
}
